import {
  QueryObserverSuccessResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Consult } from "@src/models/Consult";
import { queryIdentifiers } from "@src/utils/constants";
import { Clients } from "@src/api/v1/Clients";
import {
  RequestConsultCreateRequest,
  RequestConsultUpdateRequest,
  RequestUpdateSessionNotesRequest,
  ResponseConsultsConsultWithShortNotesResponse,
  ResponseConsultsNoteAttachmentFile,
} from "@src/api/v1/data-contracts";
import { Consults } from "@src/api/v1/Consults";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";

export const getConsultById = (clientId: string, consultId: number) =>
  new Clients(OpenUpHttpClient).getClientConsultWithNotes(consultId, clientId);

export const useClientConsultsQuery = (clientId: string | undefined) =>
  useQuery({
    queryKey: ["client-consults", clientId],
    queryFn: () => new Clients(OpenUpHttpClient).getClientConsults(clientId!),
    enabled: !!clientId,
    suspense: false,
  }) as QueryObserverSuccessResult<
    ResponseConsultsConsultWithShortNotesResponse[]
  >;

export const useConsultQuery = (clientId: string, consultId: number) => {
  return useQuery({
    queryKey: ["client-consults", clientId, consultId],
    queryFn: () => getConsultById(clientId, consultId),
    enabled: !!(clientId && consultId),
  });
};

export const useGetClosestClientConsult = (clientId: string | undefined) =>
  useQuery({
    queryKey: ["client-consults", clientId, "closest-consult"],
    queryFn: () =>
      new Clients(OpenUpHttpClient)
        .getClosestClientConsult(clientId!)
        .catch(() => null),
    enabled: !!clientId,
  });

export const useConsultNotesMutation = (
  clientId: string,
  consultId: number,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: RequestUpdateSessionNotesRequest) =>
      new Consults(OpenUpHttpClient).addOUpdateSessionNote(consultId, value),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["client-consults", clientId, consultId],
      });
    },
  });
};

export const useDeleteSessionNoteMutation = (
  clientId: string,
  consultId: number,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: string[]) =>
      new Consults(OpenUpHttpClient).deleteSessionNote(consultId, value),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["client-consults", clientId, consultId],
      });
    },
  });
};

export const useConsultMutation = (clientId: string) => {
  const queryClient = useQueryClient();
  const onSettled = () => {
    queryClient.invalidateQueries({
      queryKey: ["client-consults", clientId],
    });
    queryClient.invalidateQueries({
      queryKey: [queryIdentifiers.psychologistManagement],
    });
    queryClient.invalidateQueries({
      queryKey: [queryIdentifiers.consults],
    });
  };

  const createOrUpdate = useMutation({
    mutationFn: (
      consult: { id: number } & (
        | RequestConsultUpdateRequest
        | RequestConsultCreateRequest
      ),
    ) =>
      consult.id
        ? new Clients(OpenUpHttpClient).updateConsult(
            consult.id,
            clientId,
            consult as RequestConsultUpdateRequest,
          )
        : new Clients(OpenUpHttpClient).createConsult(
            clientId,
            consult as RequestConsultCreateRequest,
          ),
    onSettled,
  });

  const updateConsult = useMutation({
    mutationFn: (consult: { id: number } & RequestConsultUpdateRequest) =>
      new Clients(OpenUpHttpClient).updateConsult(
        consult.id,
        clientId,
        consult,
      ),
    onSettled,
  });
  const updateConsultStatus = useMutation({
    mutationFn: (consult: Consult) =>
      new Consults(OpenUpHttpClient).updateConsultStatus(
        consult.id,
        consult.status,
      ),
    onSettled,
  });

  const updateConsultTheme = useMutation({
    mutationFn: (consult: Consult) =>
      new Clients(OpenUpHttpClient).updateConsultTheme(
        consult.id,
        consult.themeKey,
        clientId,
      ),
    onSettled,
  });

  const createConsult = useMutation({
    mutationFn: (consult: RequestConsultCreateRequest) =>
      new Clients(OpenUpHttpClient).createConsult(clientId, consult),
    onSettled,
  });

  return {
    createOrUpdate,
    updateConsult,
    createConsult,
    updateConsultStatus,
    updateConsultTheme,
  };
};

export const useSessionNoteAttachmentsQuery = (consultId: number) =>
  useQuery({
    queryKey: ["consults", consultId, "attachments"],
    queryFn: async () =>
      new Consults(OpenUpHttpClient)
        .getSessionNoteAttachments(consultId)
        .catch((error) => (error.response.status === 404 ? [] : null)),
    suspense: false,
    enabled: !!consultId,
  }) as QueryObserverSuccessResult<ResponseConsultsNoteAttachmentFile[]>;

export const uploadSessionNoteAttachmentMutation = (consultId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      new Consults(OpenUpHttpClient).uploadSessionNoteAttachment(
        consultId,
        formData,
        {
          headers: {
            "Content-Type": `multipart/form-data`,
          },
        },
      ),

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["consults", consultId, "attachments"],
      });
    },
  });
};
export const deleteSessionNoteAttachmentMutation = (consultId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileName: string) =>
      new Consults(OpenUpHttpClient).deleteSessionNoteAttachment(
        consultId,
        fileName,
      ),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["consults", consultId, "attachments"],
      });
    },
  });
};

export const connectTranscription = async (
  consultId: number,
  serverCallId: string,
) =>
  new Consults(OpenUpHttpClient).connectTranscription({
    consultId,
    serverCallId,
  });

export const getTranscriptionSummary = async (consultId: number) =>
  new Consults(OpenUpHttpClient).getTranscriptionSummary(consultId);
